import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { Tab, Tabs, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { TaskModel, TaskStatus } from "../../Models/Models";
import styles from "../../Styles/tasksDashboard.module.css";
import { TASK_STATUS_COLUMN_CONFIG } from "../../Utils/tasksConstants";
import { Vocabulary } from "../../Utils/Vocabulary";
import TaskCard from "./TaskCard";

type ColumnProps = {
  status: TaskStatus;
  label: string;
  color: string;
  accentColor: string;
  badgeColor: string;
  bgColor: string;
  tasks: TaskModel[];
  onEdit: (task: TaskModel) => void;
  onDelete: (task: TaskModel) => void;
  onAdd: (status: TaskStatus) => void;
  newTaskIds?: Set<number>;
};

function KanbanColumn({
  status,
  label,
  color,
  accentColor,
  badgeColor,
  bgColor,
  tasks,
  onEdit,
  onDelete,
  onAdd,
  newTaskIds,
}: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status });

  return (
    <div
      ref={setNodeRef}
      className={styles.column}
      style={{ borderTop: `5px solid ${accentColor}`, borderRadius: 12 }}
    >
      <div className={styles.columnHeader}>
        <div className={styles.columnHeaderLeft}>
          <Typography
            variant="subtitle2"
            style={{ color, fontWeight: 600, lineHeight: 1 }}
          >
            {label}
          </Typography>
          <span className={styles.columnBadge} style={{ background: badgeColor }}>
            {tasks.length}
          </span>
        </div>
      </div>

      <div
        className={`${styles.columnBody} ${isOver ? styles.columnBodyOver : ""}`}
        style={{ background: isOver ? undefined : bgColor }}
      >
        <SortableContext
          items={tasks.map((t) => t.id!)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.length === 0 ? (
            <div className={styles.emptyColumn}>
              <AssignmentIcon style={{ fontSize: 32, opacity: 0.3 }} />
              <span>{Vocabulary.noTasks}</span>
            </div>
          ) : (
            tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onEdit={onEdit}
                onDelete={onDelete}
                isNew={newTaskIds?.has(task.id!)}
              />
            ))
          )}
        </SortableContext>

        <button className={styles.addCardButton} onClick={() => onAdd(status)}>
          <AddIcon style={{ fontSize: 16 }} />
          {Vocabulary.taskNew}
        </button>
      </div>
    </div>
  );
}

type Props = {
  tasks: TaskModel[];
  onEdit: (task: TaskModel) => void;
  onDelete: (task: TaskModel) => void;
  onAdd: (status: TaskStatus) => void;
  onStatusChange?: (task: TaskModel, status: TaskStatus) => void;
  compact?: boolean;
  newTaskIds?: Set<number>;
};

export default function TasksKanban({ tasks, onEdit, onDelete, onAdd, onStatusChange, compact, newTaskIds }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);

  if (isMobile) {
    const col = TASK_STATUS_COLUMN_CONFIG[activeTab];
    const colTasks = tasks?.filter((t) => t.status === col.status);

    return (
      <div className={styles.mobileBoard}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          className={styles.mobileTabs}
          TabIndicatorProps={{ style: { background: col.accentColor } }}
        >
          {TASK_STATUS_COLUMN_CONFIG.map((c, i) => {
            const count = tasks?.filter((t) => t.status === c.status).length;
            return (
              <Tab
                key={c.status}
                label={
                  <span className={styles.mobileTabLabel}>
                    {c.label}
                    <span
                      className={styles.mobileTabBadge}
                      style={{ background: i === activeTab ? c.badgeColor : "#9ca3af" }}
                    >
                      {count}
                    </span>
                  </span>
                }
              />
            );
          })}
        </Tabs>

        <div className={styles.mobileColumnBody} style={{ background: col.bgColor }}>
          {colTasks.length === 0 ? (
            <div className={styles.emptyColumn}>
              <AssignmentIcon style={{ fontSize: 32, opacity: 0.3 }} />
              <span>{Vocabulary.noTasks}</span>
            </div>
          ) : (
            colTasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onEdit={onEdit}
                onDelete={onDelete}
                onStatusChange={onStatusChange}
                isNew={newTaskIds?.has(task.id!)}
                mobile
              />
            ))
          )}
          <button className={styles.addCardButton} onClick={() => onAdd(col.status)}>
            <AddIcon style={{ fontSize: 16 }} />
            {Vocabulary.taskNew}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.board} ${compact ? styles.boardCompact : ""}`}>
      {TASK_STATUS_COLUMN_CONFIG.map((col) => (
        <KanbanColumn
          key={col.status}
          status={col.status}
          label={col.label}
          color={col.color}
          accentColor={col.accentColor}
          badgeColor={col.badgeColor}
          bgColor={col.bgColor}
          tasks={tasks?.filter((t) => t.status === col.status)}
          onEdit={onEdit}
          onDelete={onDelete}
          onAdd={onAdd}
          newTaskIds={newTaskIds}
        />
      ))}
    </div>
  );
}
